import { useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { getIntelligenceTrends, type TrendMonthlyRow, type TrendGmpSystemRow, type TrendRegulationRow, type TrendsParams } from '../lib/api';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { ApiErrorNotice } from '../components/ApiErrorNotice';
import { apiErrorFromResponse, shouldRetryApiError } from '../lib/api-errors';

type GroupBy = NonNullable<TrendsParams['group_by']>;
type Metric = NonNullable<TrendsParams['metric']>;

const GROUP_BY_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: 'month', label: 'Month' },
  { value: 'gmp_system', label: 'GMP System' },
  { value: 'agency', label: 'Agency' },
  { value: 'source_family', label: 'Source' },
  { value: 'regulation', label: 'Regulation' },
];

const METRIC_OPTIONS: { value: Metric; label: string }[] = [
  { value: 'finding_count', label: 'Finding Count' },
  { value: 'document_count', label: 'Document Count' },
  { value: 'unique_firms', label: 'Unique Firms' },
];

// Coverage floor labels shown for annotation
const COVERAGE_FLOOR_NOTE =
  'Coverage note: FDA Warning Letters floor = 2026-01 (posted-date). FDA Form 483 floor = 2023-08. Pre-floor periods are grey.';

// ── Inline SVG sparkline ──────────────────────────────────────────────────────
interface SparklineProps {
  readonly values: readonly number[];
  readonly width?: number;
  readonly height?: number;
}

function Sparkline({ values, width = 120, height = 32 }: SparklineProps): React.ReactElement {
  if (values.length < 2) {
    return <svg width={width} height={height} aria-hidden="true" />;
  }

  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;

  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * (width - 4) + 2;
    const y = height - 2 - ((v - min) / range) * (height - 4);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  return (
    <svg width={width} height={height} aria-hidden="true" className="inline-block">
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke="#3b82f6"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Period floor check — greys out pre-floor periods ─────────────────────────
const COVERAGE_FLOORS: Record<string, string> = {
  fda_warning_letters: '2026-01',
  fda_form_483_citations: '2023-08',
};

function isPreFloor(row: { period_month?: string; source_family?: string }): boolean {
  if (!row.period_month || !row.source_family) return false;
  const floor = COVERAGE_FLOORS[row.source_family];
  if (!floor) return false;
  return row.period_month.slice(0, 7) < floor;
}

// ── Metric accessor ────────────────────────────────────────────────────────────
function getMetricValue(row: Record<string, unknown>, metric: Metric): number {
  const v = row[metric];
  return typeof v === 'number' ? v : 0;
}

// ── Primary display label per group_by ────────────────────────────────────────
function rowLabel(row: Record<string, unknown>, groupBy: GroupBy): string {
  if (groupBy === 'month') return String(row['period_month'] ?? '—');
  if (groupBy === 'gmp_system') return String(row['gmp_system'] ?? 'Unknown');
  if (groupBy === 'regulation') return String(row['citation_type'] ?? '—');
  if (groupBy === 'agency') return String(row['agency'] ?? '—');
  if (groupBy === 'source_family') return String(row['source_family'] ?? '—');
  return '—';
}

// ── Trends response types ──────────────────────────────────────────────────────
interface TrendsResponse {
  readonly trends: readonly Record<string, unknown>[];
  readonly meta: { group_by: GroupBy; metric: Metric; window_months: number };
}

export function IntelligenceTrendsPage(): React.ReactElement {
  const { user, session } = useAuth();

  const [groupBy, setGroupBy] = useState<GroupBy>('month');
  const [metric, setMetric] = useState<Metric>('finding_count');
  const [agency, setAgency] = useState('');
  const [sourceFamily, setSourceFamily] = useState('');
  const [gmpSystem, setGmpSystem] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [windowMonths, setWindowMonths] = useState(12);

  const params: TrendsParams = {
    group_by: groupBy,
    metric,
    ...(agency ? { agency } : {}),
    ...(sourceFamily ? { source_family: sourceFamily } : {}),
    ...(gmpSystem ? { gmp_system: gmpSystem } : {}),
    ...(dateFrom ? { date_from: dateFrom } : {}),
    ...(dateTo ? { date_to: dateTo } : {}),
    window_months: windowMonths,
  };

  const cacheKey = user && session
    ? ['intelligence-trends', user.id, JSON.stringify(params)]
    : null;

  const { data, error } = useSWR<TrendsResponse>(
    cacheKey,
    async () => {
      const res = await getIntelligenceTrends(session!.access_token, params);
      const body = (await res.json().catch(() => null)) as TrendsResponse | { error: string } | null;
      if (!res.ok) {
        throw apiErrorFromResponse(res.status, body, 'Failed to load trend data.');
      }
      return body as TrendsResponse;
    },
    { shouldRetryOnError: shouldRetryApiError },
  );

  const rows = data?.trends ?? [];

  // Sparkline values for monthly view
  const sparkValues = groupBy === 'month'
    ? (rows as unknown as readonly TrendMonthlyRow[]).map((r) => Number(r[metric as keyof TrendMonthlyRow]) || 0)
    : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Intelligence: Trends</h2>
        <Link
          to="/intelligence/sources"
          className="text-sm font-medium text-sentinel-700 hover:text-sentinel-900"
        >
          ← View Sources
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="trend-group-by">
              Group by
            </label>
            <select
              id="trend-group-by"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-sentinel-500"
            >
              {GROUP_BY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="trend-metric">
              Metric
            </label>
            <select
              id="trend-metric"
              value={metric}
              onChange={(e) => setMetric(e.target.value as Metric)}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-sentinel-500"
            >
              {METRIC_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="trend-agency">
              Agency
            </label>
            <input
              id="trend-agency"
              type="text"
              value={agency}
              onChange={(e) => setAgency(e.target.value)}
              placeholder="e.g. FDA"
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sentinel-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="trend-source">
              Source
            </label>
            <input
              id="trend-source"
              type="text"
              value={sourceFamily}
              onChange={(e) => setSourceFamily(e.target.value)}
              placeholder="e.g. fda_warning_letters"
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sentinel-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="trend-gmp">
              GMP System
            </label>
            <input
              id="trend-gmp"
              type="text"
              value={gmpSystem}
              onChange={(e) => setGmpSystem(e.target.value)}
              placeholder="e.g. sterility_assurance"
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sentinel-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="trend-window">
              Window (months)
            </label>
            <input
              id="trend-window"
              type="number"
              min={1}
              max={36}
              value={windowMonths}
              onChange={(e) => setWindowMonths(Math.max(1, Math.min(36, parseInt(e.target.value, 10) || 12)))}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sentinel-500"
            />
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="trend-date-from">
              Date from
            </label>
            <input
              id="trend-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sentinel-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="trend-date-to">
              Date to
            </label>
            <input
              id="trend-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sentinel-500"
            />
          </div>
        </div>
      </div>

      {/* Sparkline for monthly trends */}
      {groupBy === 'month' && sparkValues.length >= 2 && (
        <div className="bg-white rounded-lg shadow p-4 mb-4 flex items-center gap-4">
          <div className="flex-1">
            <p className="text-xs text-slate-500 mb-1">
              {METRIC_OPTIONS.find((m) => m.value === metric)?.label} over time
            </p>
            <Sparkline values={sparkValues} width={Math.min(400, sparkValues.length * 20)} height={40} />
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-slate-900">
              {sparkValues.reduce((a, b) => a + b, 0).toLocaleString()}
            </p>
            <p className="text-xs text-slate-500">total in period</p>
          </div>
        </div>
      )}

      {/* Results table */}
      {error ? (
        <ApiErrorNotice error={error} fallbackMessage="Failed to load trend data." />
      ) : !data ? (
        <div className="text-slate-500 text-sm">Loading trend data…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-slate-400 text-sm">
          No trend data found for the selected filters. Try widening the date range or removing filters.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 text-slate-600 font-medium">
                  {groupBy === 'regulation' ? 'Citation Type' : GROUP_BY_OPTIONS.find((g) => g.value === groupBy)?.label ?? 'Period'}
                </th>
                <th className="text-left px-4 py-3 text-slate-600 font-medium">Source</th>
                <th className="text-left px-4 py-3 text-slate-600 font-medium">Agency</th>
                <th className="text-right px-4 py-3 text-slate-600 font-medium">
                  {METRIC_OPTIONS.find((m) => m.value === metric)?.label ?? metric}
                </th>
                {groupBy === 'month' && (
                  <th className="text-right px-4 py-3 text-slate-600 font-medium">Documents</th>
                )}
                <th className="text-left px-4 py-3 text-slate-600 font-medium">Coverage</th>
              </tr>
            </thead>
            <tbody>
              {(rows as readonly Record<string, unknown>[]).map((row, i) => {
                const preFloor = isPreFloor(row as Parameters<typeof isPreFloor>[0]);
                return (
                  <tr
                    key={i}
                    className={`border-t ${preFloor ? 'text-slate-400 bg-slate-50/40' : ''}`}
                  >
                    <td className="px-4 py-2.5 font-medium">
                      {rowLabel(row, groupBy)}
                      {preFloor && (
                        <span className="ml-2 text-xs text-slate-400">(pre-floor)</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {String(row['source_family'] ?? '—')}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {String(row['agency'] ?? '—')}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">
                      {getMetricValue(row, metric).toLocaleString()}
                    </td>
                    {groupBy === 'month' && (
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">
                        {Number(row['document_count'] ?? 0).toLocaleString()}
                      </td>
                    )}
                    <td className="px-4 py-2.5 text-slate-400 text-xs">
                      {String(row['earliest_event_date'] ?? '—')} –{' '}
                      {String(row['latest_event_date'] ?? '—')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Coverage floor disclosure */}
      <p className="mt-4 text-xs text-slate-400">{COVERAGE_FLOOR_NOTE}</p>
    </div>
  );
}
